import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
const categoryNames = {
    'product-addons': 'Product Addons',
    'cart-checkout': 'Cart & Checkout',
    'shipping-payment': 'Shipping & Payment',
    'marketing-sales': 'Marketing & Sales',
    'seo-performance': 'SEO & Performance',
    'customer-management': 'Customer Management',
    'inventory-management': 'Inventory Management',
    'security-compliance': 'Security & Compliance',
    'integrations-api': 'Integrations & API',
    'content-design': 'Content & Design',
    'mobile-apps': 'Mobile & Apps',
    'automation-workflows': 'Automation & Workflows',
    'reports-analytics': 'Reports & Analytics',
    'free-addons': 'Free Addons'
};
export function GlobalSearch({ addons, onAddonSelect }) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const searchRef = useRef(null);
    const inputRef = useRef(null);
    // Create searchable addon list
    const allAddons = Object.entries(addons).flatMap(([category, categoryAddons]) => categoryAddons.map(addon => (Object.assign(Object.assign({}, addon), { category, categoryName: categoryNames[category] || category }))));
    // Filter results based on search term
    useEffect(() => {
        if (!searchTerm.trim()) {
            setResults([]);
            setSelectedIndex(-1);
            return;
        }
        const filtered = allAddons.filter(addon => {
            const searchLower = searchTerm.toLowerCase();
            return (addon.name.toLowerCase().includes(searchLower) ||
                addon.description.toLowerCase().includes(searchLower) ||
                addon.categoryName.toLowerCase().includes(searchLower));
        });
        // Sort results by relevance (name matches first, then description, then category)
        filtered.sort((a, b) => {
            const searchLower = searchTerm.toLowerCase();
            const aNameMatch = a.name.toLowerCase().includes(searchLower);
            const bNameMatch = b.name.toLowerCase().includes(searchLower);
            if (aNameMatch && !bNameMatch)
                return -1;
            if (!aNameMatch && bNameMatch)
                return 1;
            return a.name.localeCompare(b.name);
        });
        setResults(filtered.slice(0, 8)); // Limit to 8 results
        setSelectedIndex(-1);
    }, [searchTerm, addons]);
    // Handle keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!isOpen)
                return;
            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    setSelectedIndex(prev => (prev < results.length - 1 ? prev + 1 : prev));
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
                    break;
                case 'Enter':
                    e.preventDefault();
                    if (selectedIndex >= 0 && results[selectedIndex]) {
                        handleAddonSelect(results[selectedIndex]);
                    }
                    break;
                case 'Escape':
                    setIsOpen(false);
                    setSearchTerm('');
                    break;
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, results, selectedIndex]);
    // Handle click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    const handleAddonSelect = (addon) => {
        onAddonSelect === null || onAddonSelect === void 0 ? void 0 : onAddonSelect(addon.category, addon.id);
        setIsOpen(false);
        setSearchTerm('');
        setSelectedIndex(-1);
    };
    const clearSearch = () => {
        var _a;
        setSearchTerm('');
        setResults([]);
        setSelectedIndex(-1);
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    const highlightMatch = (text, searchTerm) => {
        if (!searchTerm.trim())
            return text;
        const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        const parts = text.split(regex);
        return parts.map((part, index) => regex.test(part) ?
            _jsx("mark", { className: "bg-yellow-200 dark:bg-yellow-800/50 text-current rounded-sm px-0.5", children: part }, index) : part);
    };
    return (_jsxs("div", { ref: searchRef, className: "relative", children: [_jsxs("div", { className: "relative", children: [_jsxs(Button, { variant: "ghost", className: "w-full md:w-64 justify-start text-left gap-2 bg-accent/50 hover:bg-accent border border-border h-9 px-3", onClick: () => {
                            setIsOpen(true);
                            setTimeout(() => { var _a; return (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus(); }, 100);
                        }, children: [_jsx(Search, { className: "w-4 h-4 text-muted-foreground" }), _jsx("span", { className: "text-muted-foreground text-sm flex-1 truncate", children: searchTerm || 'Search addons...' }), _jsxs("kbd", { className: "hide sm:inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground ml-auto", children: [_jsx("span", { className: "text-xs", children: "\u2318" }), "K"] })] }), isOpen && (_jsx("div", { className: "absolute top-0 left-0 right-0 z-50", children: _jsxs("div", { className: "relative", children: [_jsx(Search, { className: "absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" }), _jsx(Input, { ref: inputRef, value: searchTerm, onChange: (e) => setSearchTerm(e.target.value), placeholder: "Search addons...", className: "pl-10 pr-10 h-9 bg-background border-border focus:border-primary focus:ring-2 focus:ring-primary/20", autoFocus: true }), searchTerm && (_jsx(Button, { variant: "ghost", size: "icon", className: "absolute right-1 top-1/2 transform -translate-y-1/2 w-7 h-7 hover:bg-muted", onClick: clearSearch, children: _jsx(X, { className: "w-3 h-3" }) }))] }) }))] }), isOpen && (_jsx("div", { className: "absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto", children: results.length === 0 && searchTerm.trim() ? (_jsxs("div", { className: "p-4 text-center text-muted-foreground", children: [_jsx(Search, { className: "w-8 h-8 mx-auto mb-2 opacity-50" }), _jsxs("p", { className: "text-sm", children: ["No addons found for \"", searchTerm, "\""] }), _jsx("p", { className: "text-xs mt-1", children: "Try searching for features, categories, or addon names" })] })) : results.length === 0 && !searchTerm.trim() ? (_jsxs("div", { className: "p-4 text-center text-muted-foreground", children: [_jsx(Search, { className: "w-8 h-8 mx-auto mb-2 opacity-50" }), _jsx("p", { className: "text-sm", children: "Start typing to search addons" }), _jsxs("p", { className: "text-xs mt-1", children: ["Search across ", allAddons.length, " available addons"] })] })) : (_jsxs("div", { className: "py-2", children: [results.map((addon, index) => (_jsx("button", { className: `w-full text-left px-4 py-3 hover:bg-accent transition-colors ${index === selectedIndex ? 'bg-accent' : ''}`, onClick: () => handleAddonSelect(addon), onMouseEnter: () => setSelectedIndex(index), children: _jsx("div", { className: "flex items-center justify-between gap-3", children: _jsxs("div", { className: "flex-1 min-w-0", children: [_jsxs("div", { className: "flex items-center gap-2 mb-1", children: [_jsx("span", { className: "font-medium text-sm truncate", children: highlightMatch(addon.name, searchTerm) }), _jsxs("div", { className: "flex items-center gap-1 flex-shrink-0", children: [_jsx(Badge, { variant: addon.type === 'premium' ? 'default' : 'secondary', className: "text-xs px-2 py-0", children: addon.type }), addon.enabled && (_jsx("div", { className: "w-2 h-2 bg-green-500 rounded-full" }))] })] }), _jsx("p", { className: "text-xs text-muted-foreground line-clamp-1", children: highlightMatch(addon.description, searchTerm) }), _jsxs("p", { className: "text-xs text-muted-foreground/70 mt-1", children: ["in ", highlightMatch(addon.categoryName, searchTerm)] })] }) }) }, `${addon.category}-${addon.id}`))), results.length === 8 && (_jsx("div", { className: "px-4 py-2 text-xs text-muted-foreground text-center border-t border-border bg-muted/30", children: "Showing first 8 results. Refine your search for more specific results." }))] })) }))] }));
}
